import React from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import { Container } from '@pella/sash-framework';
import { useSelector } from 'react-redux';
import { Layout, SEO, Breadcrumbs } from '../../components';

import { useBranchShowrooms } from '../../hooks/use-branch-showrooms';
import { useBranchData } from '../../hooks/use-branch-data';
import { isValidArray } from '../../components/utilities/validations';

import BranchPromoPanel from '../../components/common/branch-promo-panel';
import CardSection from '../../components/common/card-section';
import RTAPanel from '../../components/common/rta-panel';
import NearbyShowrooms from '../../components/template/branch-showroom/nearby-showrooms';

const Promotions = (props) => {
	const branches = useBranchData();
	const pageData = props.data.contentstackPromotions;
	const locations = useSelector((state) => state.locations);
	const currentBranch = branches.find((branch) => branch.branch_retail?.includes(locations?.market?.retail_branch_id) && branch.state?.abbreviation === locations?.showrooms?.[0]?.state);
	const { showrooms, error } = useBranchShowrooms(currentBranch?.branch_retail, null, currentBranch?.additional_showrooms);

	return (
		<Layout id='template-Promotions'>
			{pageData.breadcrumbs && (
				<Breadcrumbs
					data={pageData.breadcrumbs}
					currentUrl={pageData.url}
				/>
			)}

			{pageData.hero && (
				<div className='promoHero pb-5 md:pb-0'>
					<Container>
						{pageData.hero.heading && (
							<h1
								className='mb-2 font-bold md:mb-3'
								dangerouslySetInnerHTML={{ __html: pageData.hero.heading }}
							/>
						)}
						{pageData.hero.description && (
							<div
								className='md:text-xl'
								dangerouslySetInnerHTML={{ __html: pageData.hero.description }}
							/>
						)}
					</Container>
				</div>
			)}

			{isValidArray(pageData.modular_blocks) &&
				pageData.modular_blocks.map((block, index) => {
					if (block.local_promo != null) {
						return (
							<div
								className='md:py-6 xl:pb-8'
								key={index}
							>
								<Container className='max-md:px-0'>
									<BranchPromoPanel
										id={block.local_promo.anchor_text}
										branchID={currentBranch?.branch_retail?.[0]}
									/>
								</Container>
							</div>
						);
					}

					if (block.cards != null) {
						return (
							<CardSection
								data={block.cards}
								key={index}
								className='[&_h2]:xl:text-26px'
							/>
						);
					}

					if (block.rta != null) {
						return (
							<RTAPanel
								data={{ heading: block.rta.heading }}
								className={`${block.rta.gray_background ? 'bg-gray-5' : ''} py-6 xl:py-8`}
								key={index}
								boxBg={null}
								src='promotions'
							/>
						);
					}

					if (block.nearby != null && isValidArray(showrooms)) {
						return (
							<Container
								key={index}
								className='py-6 xl:py-8'
							>
								{block.nearby.heading && (
									<h2
										className='mb-2 text-xl font-semibold xl:text-26px'
										dangerouslySetInnerHTML={{ __html: block.nearby.heading }}
									/>
								)}

								<NearbyShowrooms
									key={showrooms?.length}
									error={error}
									locations={showrooms.filter((showroom) => (currentBranch?.by_state ? showroom.state === currentBranch?.state?.abbreviation : true))}
								/>
							</Container>
						);
					}
				})}
		</Layout>
	);
};

Promotions.propTypes = {
	data: PropTypes.object,
};

export default Promotions;

// eslint-disable-next-line react/prop-types -- gatsby component
export const Head = ({ data }) => <SEO {...data.contentstackPromotions.seo} />;

export const pageQuery = graphql`
	query PromotionsQuery($uid: String!) {
		contentstackPromotions(id: { eq: $uid }) {
			url
			breadcrumbs {
				...Breadcrumbs
			}
			hero {
				heading
				description
			}
			modular_blocks {
				local_promo {
					anchor_text
					heading
				}
				rta {
					gray_background
					heading
					subheading
					description
				}
				cards {
					...CardSection
				}
				nearby {
					heading
				}
			}
			seo {
				...SEO
			}
		}
	}
`;
